import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Gift } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtEUR, fmtQty, nowPeriod, ymStr } from "@/lib/format";
import { EmptyState, Loading, SimpleSelect, inputCls } from "@/components/app/common";
import { MarcaBadge } from "@/components/app/MarcaSelect";

const pad = (n) => String(n).padStart(2, "0");
const lastDay = (y, m) => new Date(y, m, 0).getDate();

export const defaultOmaggiFilter = () => {
  const { anno, mese } = nowPeriod();
  return { causali: [], mode: "mese", mese: ymStr(anno, mese), anno, dal: `${ymStr(anno, mese)}-01`, al: `${ymStr(anno, mese)}-${pad(lastDay(anno, mese))}` };
};

export function omaggiParams(f) {
  let dal = f.dal, al = f.al;
  if (f.mode === "mese") { const [y, m] = f.mese.split("-").map(Number); dal = `${f.mese}-01`; al = `${f.mese}-${pad(lastDay(y, m))}`; }
  if (f.mode === "anno") { dal = `${f.anno}-01-01`; al = `${f.anno}-12-31`; }
  return { dal, al, ...(f.causali.length ? { causali: f.causali.join(",") } : {}) };
}

const chip = (on) => `inline-flex items-center gap-1.5 text-xs px-3 h-8 rounded-full border transition-colors ${on ? "bg-[#8C6D62] border-[#8C6D62] text-white" : "bg-white border-[#EAE3DC] text-[#5E4A42] hover:bg-[#F4EFEA]"}`;

export function OmaggiFiltri({ f, setF }) {
  const [causali, setCausali] = useState([]);
  useEffect(() => { api.get("/categorie", { params: { tipo: "uscita" } }).then((r) => setCausali(r.data.filter((c) => c.gruppo === "omaggio").map((c) => c.nome))); }, []);
  const toggle = (c) => setF({ ...f, causali: f.causali.includes(c) ? f.causali.filter((x) => x !== c) : [...f.causali, c] });
  const set = (k) => (v) => setF({ ...f, [k]: v });
  return (
    <div className="card-soft px-5 py-4 mb-5 space-y-3" data-testid="omaggi-filtri">
      <div className="flex flex-wrap items-center gap-2" data-testid="omaggi-causali-select">
        <span className="text-[11px] uppercase tracking-wider text-[#756B64] font-semibold mr-1">Causali</span>
        <button type="button" data-testid="omaggi-causale-tutti" className={chip(!f.causali.length)} onClick={() => set("causali")([])}>Tutti</button>
        {causali.map((c) => <button type="button" key={c} data-testid={`omaggi-causale-chip-${c.replace(/\W+/g, "-").toLowerCase()}`} className={chip(f.causali.includes(c))} onClick={() => toggle(c)}><Gift className="h-3 w-3" />{c}</button>)}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wider text-[#756B64] font-semibold mr-1">Periodo</span>
        <SimpleSelect testId="omaggi-periodo-mode" className="w-40" value={f.mode} onChange={set("mode")} options={[{ value: "mese", label: "Mese" }, { value: "anno", label: "Anno" }, { value: "range", label: "Intervallo date" }]} />
        {f.mode === "mese" && <Input type="month" data-testid="omaggi-mese-input" value={f.mese} onChange={(e) => e.target.value && set("mese")(e.target.value)} className={`${inputCls} w-44`} />}
        {f.mode === "anno" && <Input type="number" data-testid="omaggi-anno-input" value={f.anno} onChange={(e) => set("anno")(Number(e.target.value))} className={`${inputCls} w-28`} />}
        {f.mode === "range" && <>
          <Input type="date" data-testid="omaggi-dal-input" value={f.dal} onChange={(e) => set("dal")(e.target.value)} className={`${inputCls} w-44`} />
          <span className="text-sm text-[#756B64]">→</span>
          <Input type="date" data-testid="omaggi-al-input" value={f.al} onChange={(e) => set("al")(e.target.value)} className={`${inputCls} w-44`} />
        </>}
      </div>
    </div>
  );
}

function TotaliBar({ t, isAdmin, dal, al }) {
  return (
    <div className="card-soft px-5 py-4 mb-5 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="omaggi-totali-bar">
      <div><div className="text-[11px] uppercase tracking-wider text-[#756B64] font-semibold">Pezzi omaggiati</div><div className="text-2xl font-semibold tabular" data-testid="omaggi-tot-pezzi">{fmtQty(t.qty)}</div></div>
      {isAdmin && <div><div className="text-[11px] uppercase tracking-wider text-[#756B64] font-semibold">Valore (costo medio)</div><div className="text-2xl font-semibold tabular" data-testid="omaggi-tot-valore">{fmtEUR(t.valore)}</div></div>}
      <div className="text-xs text-[#756B64]">{fmtDate(dal)} – {fmtDate(al)} · {t.n_prodotti} prodotti</div>
      <div className="basis-full flex flex-wrap gap-2" data-testid="omaggi-tot-per-causale">
        {t.per_causale.map((c, i) => <span key={c.causale} data-testid={`omaggi-tot-causale-${i}`} className="text-xs px-3 py-1.5 rounded-full bg-[#E8C5C8]/50 text-[#7A4E52]"><b>{c.causale}</b> · {fmtQty(c.qty)} pz{isAdmin ? ` · ${fmtEUR(c.valore)}` : ""}</span>)}
      </div>
    </div>
  );
}

function MovRows({ r, cols, isAdmin }) {
  return r.movimenti.map((m) => (
    <tr key={m.id} className="bg-[#FAF7F5] text-xs" data-testid={`omaggi-mov-${m.id}`}>
      <td className="px-4 py-2 tabular text-[#756B64]">{fmtDate(m.data)}</td>
      <td className="px-4 py-2 font-medium" colSpan={2}>{m.causale}</td>
      <td className="px-4 py-2 text-[#5E4A42]" colSpan={cols - (isAdmin ? 5 : 4)}>{m.destinatario || "—"}</td>
      <td className="px-4 py-2 text-right tabular">{fmtQty(m.quantita)} pz</td>
      {isAdmin && <td className="px-4 py-2 text-right tabular whitespace-nowrap">{fmtEUR(m.valore)}</td>}
    </tr>
  ));
}

export function OmaggiMagazzino({ f, filters, isAdmin }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/magazzino/omaggi", { params: { ...omaggiParams(f), ...filters } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [f, filters]);
  if (!data) return <Loading />;
  const cs = data.causali;
  const heads = ["SKU", "Prodotto", "Marca", "Tipologia", ...cs, "Totale pz", ...(isAdmin ? ["Valore"] : [])];
  return (
    <>
      <TotaliBar t={data.totali} isAdmin={isAdmin} dal={data.dal} al={data.al} />
      {!data.items.length ? <EmptyState icon={Gift} title="Nessun omaggio" text="Nessun omaggio per le causali e il periodo selezionati." testId="omaggi-empty" /> : (
        <div className="card-soft overflow-x-auto" data-testid="omaggi-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-[#FAF7F5] text-[11px] uppercase tracking-wider text-[#756B64]">
              <tr>{heads.map((h, i) => <th key={h} className={`font-semibold px-4 py-3 ${i < 4 ? "text-left" : "text-right"}`}>{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-[#EAE3DC]">
              {data.items.map((r, i) => (
                <Fragment key={r.id}>
                  <tr data-testid={`omaggi-row-${i}`} onClick={() => setOpen(open === r.id ? null : r.id)} className="cursor-pointer hover:bg-[#FAF7F5] transition-colors">
                    <td className="px-4 py-3 text-xs tabular text-[#756B64]"><span className="inline-flex items-center gap-1">{open === r.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}{r.sku || "—"}</span></td>
                    <td className="px-4 py-3 font-medium">{r.nome}</td>
                    <td className="px-4 py-3"><MarcaBadge nome={r.marca} /></td>
                    <td className="px-4 py-3 text-xs">{r.linea}</td>
                    {cs.map((c) => <td key={c} className="px-4 py-3 text-right tabular text-[#5E4A42]">{r.per_causale[c] ? fmtQty(r.per_causale[c]) : "—"}</td>)}
                    <td className="px-4 py-3 text-right tabular font-semibold" data-testid={`omaggi-qty-${i}`}>{fmtQty(r.qty)}</td>
                    {isAdmin && <td className="px-4 py-3 text-right tabular whitespace-nowrap" data-testid={`omaggi-valore-${i}`}>{fmtEUR(r.valore)}</td>}
                  </tr>
                  {open === r.id && <MovRows r={r} cols={heads.length} isAdmin={isAdmin} />}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
